import { Link } from "react-router-dom";
import { XMarkIcon } from "@heroicons/react/24/outline";

import TextWithLabel from "../../../controls/TextWithLabel";
import LocationZoneStacked from "../../../packaging/LocationZoneStacked";
import {
  formatHistoryDateTime,
  getHistoryActionBadgeClass,
} from "./packagingBarcodeHistoryUtils";

function TransferContextBlock({ context }) {
  const fromZone = context?.from_zone?.label;
  const fromLocation = context?.from_location?.label;
  const toZone = context?.to_zone?.label;
  const toLocation = context?.to_location?.label;

  if (!fromZone && !fromLocation && !toZone && !toLocation) {
    return null;
  }

  return (
    <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
        Transfer
      </p>
      <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <p className="text-xs font-medium text-gray-500">From</p>
          <LocationZoneStacked
            row={{
              zone: context?.from_zone,
              location: context?.from_location,
            }}
          />
        </div>
        <div>
          <p className="text-xs font-medium text-gray-500">To</p>
          <LocationZoneStacked
            row={{
              zone: context?.to_zone,
              location: context?.to_location,
            }}
          />
        </div>
      </div>
    </div>
  );
}

export default function PackagingBarcodeHistoryEventDetail(props) {
  const { event, serialNumber, setOpen } = props;
  const shipping = event?.context?.shipping;
  const orderItem = event?.context?.order_item;
  const metadata = event?.metadata;
  const eventDateTime = formatHistoryDateTime(event?.created_at);
  const isTransferEvent = String(event?.action || "").toLowerCase() === "transfer";
  const hasTransferContext =
    isTransferEvent &&
    (event?.context?.from_location ||
      event?.context?.from_zone ||
      event?.context?.to_location ||
      event?.context?.to_zone);

  const operatorName =
    event?.operator?.label ||
    event?.operator?.name ||
    (typeof event?.operator === "string" ? event.operator : null);

  const displaySerialNumber = event?.serial_number ?? serialNumber ?? null;

  const handleClose = () => setOpen({ childComponent: null, open: false });

  return (
    <div className="flex h-full flex-col bg-white">
      <div className="sticky top-0 z-10 border-b border-gray-200 bg-white px-4 py-4 sm:px-6">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <span
                className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${getHistoryActionBadgeClass(event?.action)}`}
              >
                {(event?.action || "unknown").replace(/_/g, " ")}
              </span>
              <span className="inline-flex rounded-full bg-purple-100 px-2.5 py-1 text-xs font-semibold text-purple-700">
                {(event?.type || "unknown").replace(/_/g, " ")}
              </span>
            </div>
            <p className="text-sm text-gray-500">
              {eventDateTime.timeText
                ? `${eventDateTime.dateText}, ${eventDateTime.timeText}`
                : eventDateTime.dateText}
            </p>
            {operatorName ? (
              <p className="text-sm font-medium text-gray-900">
                Operator: {operatorName}
              </p>
            ) : null}
          </div>
          <button
            type="button"
            className="rounded-md p-1.5 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            onClick={handleClose}
          >
            <span className="sr-only">Close panel</span>
            <XMarkIcon className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-4 sm:px-6">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <TextWithLabel
            label="Barcode"
            text={event?.barcode || "N/A"}
            parentClass="sm:col-span-2"
            textClass="break-all text-sm font-medium text-gray-900"
          />
          <TextWithLabel
            label="Serial Number"
            text={displaySerialNumber || "N/A"}
          />
          <TextWithLabel
            label="Operator Name"
            text={operatorName || "N/A"}
          />
          <TextWithLabel
            label="Amount"
            text={event?.amount != null ? String(event.amount) : "N/A"}
          />
          <TextWithLabel
            label="Reason"
            text={event?.reason || "N/A"}
            parentClass="sm:col-span-2"
            textClass="break-words text-sm font-medium text-gray-900"
          />
        </div>

        {hasTransferContext ? (
          <TransferContextBlock context={event.context} />
        ) : null}

        {shipping || orderItem ? (
          <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-3">
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Shipping Context
              </p>
              {shipping?.id ? (
                <Link
                  className="rounded-md bg-main-purple px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-indigo-600"
                  to={`/shippings/${shipping.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View
                </Link>
              ) : null}
            </div>
            <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2">
              <TextWithLabel
                label="PO Number"
                text={shipping?.po_number || "N/A"}
              />
              <TextWithLabel
                label="Invoice Number"
                text={shipping?.invoice_number || "N/A"}
              />
              <TextWithLabel
                label="Order Item Product"
                text={orderItem?.product_name || "N/A"}
              />
            </div>
          </div>
        ) : null}

        {metadata ? (
          <div className="mt-4 rounded-lg border border-gray-200 bg-white p-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Metadata
            </p>
            <pre className="mt-2 overflow-auto rounded-md bg-slate-900 p-3 text-xs text-slate-100">
              {JSON.stringify(metadata, null, 2)}
            </pre>
          </div>
        ) : null}
      </div>

      <div className="border-t border-gray-200 bg-white px-4 py-4 sm:px-6">
        <div className="flex justify-end">
          <button
            type="button"
            className="rounded-md bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 transition hover:bg-gray-50"
            onClick={handleClose}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
